import { type Amount, newAmount } from '@orus.eu/amount'

import type { InsuranceDeductible } from '../../../model/policy-item.ts'
import type { FrAupaDeductibleLevel, FrAupaPolicyItem, FrAupaPolicyItemsState } from '../fr-aupa-insurance.ts'

/**
 * Transcription of the SPECIFIC_DEDUCTIBLES tab of the product sheet: one row per SRA class, one column per
 * deductible level. It serves fire, theft, all-accident damage, climate events and terrorism, but the reduced
 * column only the first three: the reduced deductible does not apply to climate events and terrorism.
 */
const generalDeductibleBySraClass = {
  A: { standard: newAmount(340), reduced: newAmount(170) },
  B: { standard: newAmount(340), reduced: newAmount(170) },
  C: { standard: newAmount(340), reduced: newAmount(170) },
  D: { standard: newAmount(370), reduced: newAmount(190) },
  E: { standard: newAmount(370), reduced: newAmount(190) },
  F: { standard: newAmount(370), reduced: newAmount(190) },
  G: { standard: newAmount(370), reduced: newAmount(190) },
  H: { standard: newAmount(380), reduced: newAmount(190) },
  I: { standard: newAmount(400), reduced: newAmount(200) },
  J: { standard: newAmount(415), reduced: newAmount(210) },
  K: { standard: newAmount(485), reduced: newAmount(245) },
  L: { standard: newAmount(550), reduced: newAmount(275) },
  M: { standard: newAmount(615), reduced: newAmount(305) },
  N: { standard: newAmount(635), reduced: newAmount(320) },
  O: { standard: newAmount(765), reduced: newAmount(380) },
  P: { standard: newAmount(890), reduced: newAmount(445) },
  Q: { standard: newAmount(1145), reduced: newAmount(570) },
  R: { standard: newAmount(1400), reduced: newAmount(700) },
  S: { standard: newAmount(1780), reduced: newAmount(890) },
  T: { standard: newAmount(2035), reduced: newAmount(1015) },
  U: { standard: newAmount(2290), reduced: newAmount(1145) },
} as const satisfies Record<string, Record<FrAupaDeductibleLevel, Amount>>

type SraClassInTable = keyof typeof generalDeductibleBySraClass

/**
 * The highest row stands in whenever the table cannot answer: it is the only value the real class can lower but
 * never raise. Two states reach it. The vehicle is not known yet — the policy items are written as soon as the
 * formula is chosen, and no funnel order guarantees the vehicle comes first. Or the class is outside A–U, which the
 * vehicle catalogue does carry (HC, V to ZA): the `SRA_class_too_high` exclusion refuses the sale at rating time,
 * downstream of this catalogue, so crashing here would hide the exclusion behind an error.
 */
const CLASS_WHEN_THE_TABLE_CANNOT_ANSWER: SraClassInTable = 'U'

/** The default value of `franchise_level` in the INPUT tab of the product sheet. */
const DEFAULT_LEVEL: FrAupaDeductibleLevel = 'standard'

function isSraClassInTable(letter: string): letter is SraClassInTable {
  return Object.hasOwn(generalDeductibleBySraClass, letter)
}

function sraClassOf(sraClass: string | null | undefined): SraClassInTable {
  return sraClass && isSraClassInTable(sraClass) ? sraClass : CLASS_WHEN_THE_TABLE_CANNOT_ANSWER
}

export const FR_AUPA_POLICY_ITEMS_FOLLOWING_DEDUCTIBLE_LEVEL_V1_0_0 = [
  'fire',
  'theft',
  'allAccidentDamage',
] as const satisfies readonly FrAupaPolicyItem[]

export function getFrAupaGeneralDeductibleByLevel_v1_0_0({
  vehicleData,
}: FrAupaPolicyItemsState): Record<FrAupaDeductibleLevel, Amount> {
  return generalDeductibleBySraClass[sraClassOf(vehicleData?.sraClass)]
}

export function getFrAupaGeneralDeductible_v1_0_0(state: FrAupaPolicyItemsState): InsuranceDeductible {
  return {
    type: 'amounts',
    value: getFrAupaGeneralDeductibleByLevel_v1_0_0(state)[state.frAupaDeductibleLevel ?? DEFAULT_LEVEL],
  }
}

export function getFrAupaStandardGeneralDeductible_v1_0_0({
  vehicleData,
}: FrAupaPolicyItemsState): InsuranceDeductible {
  return getFrAupaGeneralDeductible_v1_0_0({ vehicleData, frAupaDeductibleLevel: 'standard' })
}
